import React, { useState, useEffect } from 'react';
import logger from '../utils/logger';
import { setVisibleInterval } from '../utils/visibleInterval';
import { Download, Trash2, RefreshCw, Filter } from 'lucide-react';

const LogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState({ level: 'all', category: 'all' });
  const [stats, setStats] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    loadLogs();
    const interval = autoRefresh ? setVisibleInterval(loadLogs, 2000) : null;
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when filter, autoRefresh change; loadLogs is defined in render and reads the latest state
  }, [filter, autoRefresh]);

  const loadLogs = () => {
    const filters = {};
    if (filter.level !== 'all') filters.level = filter.level;
    if (filter.category !== 'all') filters.category = filter.category;
    
    const filteredLogs = logger.getLogs(filters);
    setLogs(filteredLogs);
    setStats(logger.getStats());
  };

  const getLevelColor = (level) => {
    const colors = {
      debug: '#6b7280',
      info: '#3b82f6',
      success: '#10b981',
      warn: '#f59e0b',
      error: '#ef4444',
      system: '#8b5cf6'
    };
    return colors[level] || '#000';
  };

  const categories = [...new Set(logs.map(log => log.category))];

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      right: 0,
      width: '600px',
      height: '400px',
      backgroundColor: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px 8px 0 0',
      boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      fontSize: '12px',
      zIndex: 9999
    }}>
      {/* Header */}
      <div style={{
        padding: '12px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#f9fafb'
      }}>
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>
          Log Viewer ({logs.length} logs)
        </h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          <button
            onClick={loadLogs}
            style={{
              padding: '4px 8px',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
              background: '#fff',
              cursor: 'pointer'
            }}
          >
            <RefreshCw size={14} />
          </button>
          <button
            onClick={() => logger.exportLogs()}
            style={{
              padding: '4px 8px',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
              background: '#fff',
              cursor: 'pointer'
            }}
          >
            <Download size={14} />
          </button>
          <button
            onClick={() => {
              logger.clearLogs();
              loadLogs();
            }}
            style={{
              padding: '4px 8px',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
              background: '#fff',
              cursor: 'pointer'
            }}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        padding: '8px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        backgroundColor: '#f9fafb'
      }}>
        <Filter size={14} />
        <select
          value={filter.level}
          onChange={(e) => setFilter({ ...filter, level: e.target.value })}
          style={{
            padding: '4px',
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            fontSize: '12px'
          }}
        >
          <option value="all">All Levels</option>
          <option value="debug">Debug</option>
          <option value="info">Info</option>
          <option value="success">Success</option>
          <option value="warn">Warning</option>
          <option value="error">Error</option>
          <option value="system">System</option>
        </select>
        <select
          value={filter.category}
          onChange={(e) => setFilter({ ...filter, category: e.target.value })}
          style={{
            padding: '4px',
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            fontSize: '12px'
          }}
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div style={{
        padding: '8px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        gap: '16px',
        fontSize: '11px',
        color: '#6b7280'
      }}>
        <span>Total: {stats.total}</span>
        {Object.entries(stats.byLevel || {}).map(([level, count]) => (
          <span key={level} style={{ color: getLevelColor(level) }}>
            {level}: {count}
          </span>
        ))}
      </div>

      {/* Logs */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '8px',
        fontFamily: 'monospace'
      }}>
        {logs.slice(-100).reverse().map((log, i) => (
          <div
            key={i}
            style={{
              marginBottom: '4px',
              padding: '4px',
              borderRadius: '4px',
              backgroundColor: log.level === 'error' ? '#fee2e2' : '#f9fafb',
              fontSize: '11px',
              lineHeight: '1.4'
            }}
          >
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ color: '#6b7280' }}>
                {new Date(log.timestamp).toLocaleTimeString()}
              </span>
              <span
                style={{
                  color: getLevelColor(log.level),
                  fontWeight: 600,
                  textTransform: 'uppercase'
                }}
              >
                {log.level}
              </span>
              <span style={{ color: '#8b5cf6', fontWeight: 600 }}>
                [{log.category}]
              </span>
              <span style={{ flex: 1 }}>{log.message}</span>
            </div>
            {log.data && Object.keys(log.data).length > 0 && (
              <div style={{
                marginTop: '2px',
                marginLeft: '20px',
                color: '#6b7280',
                fontSize: '10px'
              }}>
                {JSON.stringify(log.data, null, 2)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LogViewer;